import { useEffect, useState } from 'react';
import {
  CONSENT_TYPES,
  LEGAL_VERSION,
  checkConsent,
  recordConsent,
} from '../services/api';

interface ConsentGateProps {
  /** Chamado quando o usuario esta em conformidade (consentimento OK). */
  onResolved: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  terms: 'Termos de Uso',
  privacy: 'Politica de Privacidade',
  cookies: 'Politica de Cookies',
};

function ConsentGate({ onResolved }: ConsentGateProps) {
  const [checking, setChecking] = useState(true);
  const [needs, setNeeds] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    checkConsent()
      .then((res) => {
        if (!active) return;
        if (res.needs_consent) {
          setNeeds(true);
          const accepted = new Set(res.accepted_types);
          setMissing(CONSENT_TYPES.filter((t) => !accepted.has(t)));
        } else {
          onResolved();
        }
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Erro ao verificar consentimento.');
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [onResolved]);

  async function handleAccept() {
    setError(null);
    if (!accept) {
      setError('E necessario aceitar os documentos legais para continuar.');
      return;
    }
    setSubmitting(true);
    try {
      // Registra consentimento para todos os documentos pendentes na versao vigente.
      for (const t of missing) {
        await recordConsent(t, LEGAL_VERSION);
      }
      setNeeds(false);
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao registrar consentimento.');
    } finally {
      setSubmitting(false);
    }
  }

  if (checking) {
 return null; }
  if (!needs) {
 return null; }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-xl font-bold text-brand-900">Consentimento LGPD</h2>
        <p className="mb-4 text-sm text-brand-600">
          Para continuar utilizando o Aura Nota, e necessario aceitar a versao
          vigente ({LEGAL_VERSION}) dos seguintes documentos legais:
        </p>
        <ul className="mb-4 space-y-1 text-sm text-brand-700">
          {missing.map((t) => (
            <li key={t}>
              &middot;{' '}
              <a href={`/${t}`} className="text-brand-700 underline" target="_blank" rel="noreferrer">
                {TYPE_LABEL[t] ?? t}
              </a>
            </li>
          ))}
        </ul>

        <label className="mb-4 flex items-start gap-2 text-sm text-brand-600">
          <input
            type="checkbox"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            className="mt-1"
          />
          <span>
            Li e aceito os Termos de Uso, a Politica de Privacidade e a Politica
            de Cookies.
          </span>
        </label>

        {error && <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        <button
          type="button"
          onClick={handleAccept}
          disabled={submitting}
          className="w-full rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {submitting ? 'Registrando...' : 'Aceitar e continuar'}
        </button>
      </div>
    </div>
  );
}

export default ConsentGate;
